import { jwtTokenService } from '@mathflat/domain/@entities/Auth/jwtTokenService'
import type { NamedEnv } from '@mathflat/shared/@common/devToolbar'
import { mountDevToolbar } from '@mathflat/shared/@common/devToolbar'
import { Observer, observer } from 'mobx-react'
import { useEffect } from 'react'
import { isDesktop } from 'react-device-detect'
import { Outlet, useNavigate } from 'react-router-dom'

import { routeName } from '~/@common/constants'
import { useStudentAppMediaQuery } from '~/@common/hooks/useMediaQuery'
import { useOrientation } from '~/@common/hooks/useOrientation'
import { useTransferDoneRedirect } from '~/@common/integration/useTransferDoneRedirect'
import { handwrittenNoteTooltipService } from '~/@common/services/HandwrittenTooltip.service'
import { remoteStorageService } from '~/@common/services/remoteStorage.service'
import { commonRepo } from '~/@common/services/repo.service'
import { VirtualKeyboardDrawer } from '~/@common/ui/(Keypad)'
import { virtualKeyboardService } from '~/@common/ui/(Keypad)/keypad.service'
import Modal from '~/@common/ui/modal/Modal'
import { PortalRootLayoutHeader } from '~/@common/ui/PortalRootLayoutHeader'
import SwiperProvider from '~/@common/ui/SwiperController/Swiper'
import { envUtils } from '~/@common/utils/envUtils'
import { GNB } from '~/@pages/@widgets/(Navigation)/GNB'
import SideGNB from '~/@pages/@widgets/(Navigation)/SideGNB/SideGNB'
import Main from '~/@pages/@widgets/Main'
import ScreenSizeGuide from '~/@pages/@widgets/ScreenSizeGuide'

import useApp from './hooks/useApp'
import { useDataTracking } from './hooks/useMixpanel'
import { initAxiosDefaultBaseURL } from './init/envSetting/envSettings'
import { IntegrationDevPanel } from './init/envSetting/IntegrationDevPanel'
import { openB2GSignupPopup } from './init/popup'

const App = () => {
  const { location } = useApp()
  const navigate = useNavigate()
  const { type } = useOrientation()
  const { isMobile } = useStudentAppMediaQuery()

  const desktopSmallScreenWhitelist: string[] = [routeName.login, routeName.profile]
  const isShowSmallScreenGuide =
    isDesktop && envUtils.isLive && !desktopSmallScreenWhitelist.includes(location.pathname)

  const { isParent } = commonRepo.permission
  const isSideGNBLayoutMode = !isParent && !isMobile && commonRepo.isElementarySchoolflat
  const isStagingOrLive = envUtils.isLive || envUtils.isStaging

  useEffect(() => {
    if (isStagingOrLive) return
    const handle = mountDevToolbar({
      onApiEnvApply: (url: string) => {
        jwtTokenService.clearTokens()
        if (url === 'live' || url === 'stg' || url === 'dev') {
          jwtTokenService.setEnvironment(url)
        } else {
          jwtTokenService.setEnvironment('custom', url)
        }
        initAxiosDefaultBaseURL(url)
        navigate(routeName.login)
      },
      onSecondaryEnvApply: (_env: NamedEnv, customUrl: string) => {
        jwtTokenService.clearTokens()
        initAxiosDefaultBaseURL(customUrl)
        navigate(routeName.login)
      },
    })
    return () => handle.unmount()
  }, [])

  useDataTracking()
  // ★ 통합 마이그레이션 BroadcastChannel — student-up 탭이 sender 마이그레이션 완료 알림 받으면 다른 탭도 student.* 으로 즉시 수렴
  useTransferDoneRedirect()

  useEffect(() => {
    if (type === 'portrait') {
      handwrittenNoteTooltipService.showHandwrittenInfoTooltip()
    }
  }, [type])

  useEffect(() => {
    const B2G_SIGNUP_MODAL_SIGNUP_FROM = ['WHALE_SPACE', 'NAVER_WORKS']

    remoteStorageService.get('isCompletedInitialStudentInfoSetting').then((remoteStorage) => {
      if (
        commonRepo.signUpFrom &&
        B2G_SIGNUP_MODAL_SIGNUP_FROM.includes(commonRepo.signUpFrom) &&
        !remoteStorage?.isCompletedInitialStudentInfoSetting &&
        location.pathname !== routeName.login
      ) {
        openB2GSignupPopup()
      }
    })
  }, [location.pathname])

  return (
    <>
      {/* {!envUtils.isLive && <IntegrationDevPanel />} */}
      {isShowSmallScreenGuide && <ScreenSizeGuide />}
      {location.pathname !== routeName.serverMaintenance && (
        <PortalRootLayoutHeader isSideHeaderLayoutMode={isSideGNBLayoutMode}>
          {isSideGNBLayoutMode ? <SideGNB /> : <GNB />}
        </PortalRootLayoutHeader>
      )}
      <SwiperProvider>
        <Main>
          <Outlet />
        </Main>
      </SwiperProvider>
      <Modal.Container />
      {isMobile && (
        <Observer>
          {() => (
            <VirtualKeyboardDrawer
              open={virtualKeyboardService.show && virtualKeyboardService.showType === 'drawer'}
              keypadType={virtualKeyboardService.type}
              closeKeyboard={() => virtualKeyboardService.close()}
              onKeyButtonClick={(e, latexNodeType) => {
                virtualKeyboardService.onKeyButtonClick(e, latexNodeType)
              }}
            />
          )}
        </Observer>
      )}
    </>
  )
}

export default observer(App)
